import groupBy from 'lodash.groupby'
import { useEffect, useMemo, useState } from 'react'
import { Checkbox, useMessage, useGetTeamMembers, Spin } from '@pepper/ui'
import {
  NumberParam,
  ArrayParam,
  useQueryParams,
  withDefault,
} from 'use-query-params'

import { useSelectedCompanyId } from 'utils/functions/misc'
import downloadItems from 'utils/api/downloadItems'
import { useGetWorkflowStatus } from 'utils/hooks/workflows/useGetWorkflowStatus'
import { useDocItems } from 'utils/hooks/useDocItems'
import Header from 'components/items/Header/Header'
import DocRow from 'components/items/DocRow/DocRow'
import Footer from 'components/items/Footer/Footer'
import BottomPopup from 'components/items/ItemsBottomPopup/ItemsBottomPopup'
import DocStatusLog from 'components/workflows/DocStatusLog'
import EmptyState from './EmptyState'

import s from './DocList.module.css'

type Props = {
  viewType: 'project-items' | 'all-items'
  // * * pass ids of newly created assignment/private_docs items
  recentItems?: string[]
  handleDocsDownload?: (items: Item[]) => void
  project?: Project
}

const PROJECT_PER_PAGE = 10

const List = ({
  viewType,
  recentItems,
  handleDocsDownload,
  project,
}: Props) => {
  const [selectedItemsStage, setSelectedItemsStage] = useState<
    AssignmentStage | null | ''
  >(null)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [selectedItems, setSelectedItems] = useState<Item[]>([])
  const [selectedItem, setSelectedItem] = useState<Item>()
  const companyId = useSelectedCompanyId()
  const { showError } = useMessage()
  const { data: assignees } = useGetTeamMembers({ companyId })
  const { data: workflowStatuses } = useGetWorkflowStatus({ companyId })
  const [queryParamFilters, setQueryParamFilters] = useQueryParams({
    status: withDefault(ArrayParam, []),
    assignee: withDefault(ArrayParam, []),
    currentPage: withDefault(NumberParam, 0),
    limit: withDefault(NumberParam, PROJECT_PER_PAGE),
  })
  const {
    records: items,
    total: totalDocs,
    refetch: refetchDocs,
    isLoading: isDocsLoading,
  } = useDocItems({
    companyId,
    projectId: project ? project.id : '',
    queryParams: queryParamFilters,
    pageSize: queryParamFilters.limit,
    options: {
      isEnabled: true,
    },
  })
  const { assignee, status } = queryParamFilters
  const isNoFilterApplied = assignee.length === 0 && status.length === 0
  const itemsOrder = items.map(item => item.code)

  const _getGroupedItems = (_items: Item[]): Item[] => {
    const listItems: Item[] = []
    const group = groupBy(_items, 'group_id')

    Object.keys(group).forEach(groupId => {
      listItems.push(
        ...group[groupId].map(item => {
          return {
            ...item,
            grouped: groupId !== 'null' && group[groupId].length > 1,
          }
        })
      )
    })
    const listItemsMap = groupBy(listItems, 'code')
    const sortedListItems = itemsOrder.map(itemCode => {
      return listItemsMap[itemCode][0]
    })

    return sortedListItems
  }

  const handleDownload = (_items: Item[]) => {
    try {
      downloadItems(_items, companyId, 'doc')
    } catch (error) {
      showError(error.message)
    }
  }

  const handleSelectAll = (isChecked: boolean) => {
    isChecked ? setSelectedItems([]) : setSelectedItems(items)
  }

  const onDrawerClose = () => {
    setIsDrawerOpen(false)
  }

  useEffect(() => {
    if (selectedItems.length > 1) return
    if (selectedItems.length === 1) {
      setSelectedItemsStage(selectedItems[0].stage)
      return
    }
    setSelectedItemsStage(null)
  }, [selectedItems])

  useEffect(() => {
    // Update the selected item if items list if updated
    if (!selectedItem) return
    const _item = items.find(_i => _i.id === selectedItem.id)
    if (_item) {
      setSelectedItem(_item)
    }
  }, [items])

  useEffect(() => {
    setQueryParamFilters({ limit: queryParamFilters.limit })
  }, [])

  const groupedItems: Item[] = useMemo(() => {
    return _getGroupedItems(items)
  }, [items, _getGroupedItems, itemsOrder])

  const isEmptyState = items.length === 0 && isNoFilterApplied

  const renderEmptyList = isEmptyState ? (
    <div className={s.emptyList}>
      <EmptyState />
    </div>
  ) : (
    <div className={s.emptyList}>No results found</div>
  )

  if (isDocsLoading) return <Spin className={s.spin} size="large" />

  return (
    <>
      <Header<Item>
        viewType={viewType}
        onDownload={() => handleDownload(items)}
        setSelectedItems={setSelectedItems}
        project={project}
        tabName="docs"
        showFilter={!isEmptyState}
        handleDocsDownload={() => {
          handleDocsDownload?.(items)
        }}
      />
      {groupedItems.length > 0 ? (
        <div className={s.list}>
          <div className={s.wrapper}>
            <Checkbox
              className={s.checkbox}
              onChange={() =>
                handleSelectAll(selectedItems.length === items.length)
              }
              checked={selectedItems.length === items.length}
            />
            Content Items
          </div>
          {groupedItems?.map((item: Item) => (
            <DocRow
              key={item.id}
              item={item}
              companyId={companyId}
              selectedItems={selectedItems}
              recentItems={recentItems}
              assigneeList={assignees}
              workflowStatusList={workflowStatuses}
              setItem={setSelectedItem}
              setSelectedItems={setSelectedItems}
              setOpenDrawer={setIsDrawerOpen}
              refetchItems={refetchDocs}
            />
          ))}
          <Footer
            total={totalDocs}
            queryParamFilters={queryParamFilters}
            setQueryParamFilters={setQueryParamFilters}
          />
        </div>
      ) : (
        renderEmptyList
      )}

      {selectedItems.length > 0 && (
        <div className={s.footer}>
          <BottomPopup
            items={items}
            onDownload={handleDownload}
            refetchRecords={refetchDocs}
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
            selectedItemsStage={selectedItemsStage}
            handleDocsDownload={handleDocsDownload}
            companyId={companyId}
            assigneeList={assignees}
            workflowStatusList={workflowStatuses}
          />
        </div>
      )}
      {selectedItem && (
        <DocStatusLog
          companyId={companyId}
          statusList={workflowStatuses}
          assigneeList={assignees}
          openDrawer={isDrawerOpen}
          item={selectedItem}
          setItem={setSelectedItem}
          onDrawerClose={onDrawerClose}
        />
      )}
    </>
  )
}

export default List
